import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  FileWarning,
  Globe,
  ShieldCheck,
  Undo2,
  XCircle,
} from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Modal,
  Textarea,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { RESULTS_KEY, type RankingDetail } from '../../lib/results'

const ICON = {
  ok: <CheckCircle2 aria-hidden="true" size={16} />,
  warning: <AlertTriangle aria-hidden="true" size={16} />,
  error: <XCircle aria-hidden="true" size={16} />,
}

type Dialog =
  null | 'approve' | 'reject' | 'provisional' | 'final' | 'rectify' | 'accept' | 'refuse'

/**
 * Circuit de validation (cahier §9.9) : contrôle des seuils et quotas, visas,
 * approbation par l'autorité (jamais par un auteur du classement), publication
 * provisoire puis définitive, rectification (circuit d'exception).
 */
export function ApprovalPanel({ ranking }: { ranking: RankingDetail }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<Dialog>(null)
  const open = ranking.rectifications.find((r) => ['requested', 'accepted'].includes(r.status))
  const official = ['approved', 'published'].includes(ranking.status)
  const published = (kind: 'provisional' | 'final') =>
    ranking.publications.some((p) => p.kind === kind)

  const act = useMutation({
    mutationFn: ({ url, body }: { url: string; body?: unknown; message: string }) =>
      session.api.post(url, body ?? {}).then(() => undefined),
    onSuccess: async (_, { message }) => {
      toast.success(message)
      setDialog(null)
      await queryClient.invalidateQueries({ queryKey: RESULTS_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const base = `/admin/rankings/${ranking.id}`
  const showControl = ranking.status === 'under_control' || official

  if (!showControl && !ranking.rectifications.length) return null
  const errors = ranking.checks.filter((c) => c.level === 'error')
  const warnings = ranking.checks.filter((c) => c.level === 'warning')

  return (
    <Card className="approval-card">
      <CardHeader
        title={official ? 'Classement officiel' : 'Contrôle et approbation'}
        actions={
          <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {ranking.can.reject && (
              <Button size="sm" icon={<Undo2 />} onClick={() => setDialog('reject')}>
                Renvoyer
              </Button>
            )}
            {ranking.can.approve && (
              <Button
                size="sm"
                variant="primary"
                icon={<ShieldCheck />}
                onClick={() => setDialog('approve')}
              >
                Approuver
              </Button>
            )}
            {ranking.can.publish && !published('provisional') && !published('final') && (
              <Button size="sm" icon={<Globe />} onClick={() => setDialog('provisional')}>
                Publier (provisoire)
              </Button>
            )}
            {ranking.can.publish && !published('final') && (
              <Button
                size="sm"
                variant="primary"
                icon={<Globe />}
                onClick={() => setDialog('final')}
              >
                Publier (définitif)
              </Button>
            )}
            {ranking.can.rectify && (
              <Button size="sm" icon={<FileWarning />} onClick={() => setDialog('rectify')}>
                Demander une rectification
              </Button>
            )}
          </span>
        }
      />
      <CardBody>
        <div className="approval-grid">
          {showControl && (
            <section className="stack" style={{ gap: 'var(--space-2)' }}>
              <h3 className="sel-section-title">Contrôle des seuils et quotas</h3>
              <ul className="control-list">
                {ranking.checks.map((c) => (
                  <li key={c.message} data-level={c.level}>
                    {ICON[c.level]} <span>{c.message}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="stack" style={{ gap: 'var(--space-2)' }}>
            <h3 className="sel-section-title">Visas</h3>
            <ol className="visa-list">
              {ranking.visas.map((v, i) => (
                <li key={i}>
                  <strong>{v.role}</strong>
                  <span>
                    {v.by}
                    {v.at ? ` — ${when(v.at)}` : ''}
                  </span>
                  {v.note && <span className="muted">« {v.note} »</span>}
                </li>
              ))}
              {ranking.publications.map((p) => (
                <li key={p.kind}>
                  <strong>{p.label}</strong>
                  <span>
                    {p.by} — {when(p.at)}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {ranking.status === 'under_control' && ranking.can.isAuthor && (
          <Alert tone="info">
            Vous avez préparé ce classement (calcul ou décision) : une autre personne doit
            l’approuver.
          </Alert>
        )}
        {ranking.status === 'under_control' && errors.length > 0 && (
          <Alert tone="danger">
            Approbation impossible tant que le contrôle signale une anomalie.
          </Alert>
        )}

        {ranking.rectifications.length > 0 && (
          <section className="stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
            <h3 className="sel-section-title">Rectifications</h3>
            {ranking.rectifications.map((r) => (
              <div key={r.id} className="rectification">
                <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <Badge
                    variant={
                      r.status === 'rejected'
                        ? 'neutral'
                        : r.status === 'completed'
                          ? 'success'
                          : 'warning'
                    }
                  >
                    {r.statusLabel}
                  </Badge>
                  <span className="muted">
                    Demandée par {r.requestedBy} le {when(r.requestedAt)}
                  </span>
                </div>
                <p style={{ margin: 0 }}>{r.reason}</p>
                {r.decidedBy && (
                  <p className="muted" style={{ margin: 0 }}>
                    Décision de {r.decidedBy} le {when(r.decidedAt)} : {r.decisionNote}
                  </p>
                )}
                {r.status === 'requested' && ranking.can.decideRectification && (
                  <div className="row" style={{ gap: 'var(--space-2)' }}>
                    <Button size="sm" onClick={() => setDialog('refuse')}>
                      Refuser
                    </Button>
                    <Button size="sm" variant="primary" onClick={() => setDialog('accept')}>
                      Accepter
                    </Button>
                  </div>
                )}
                {r.status === 'accepted' && (
                  <p className="muted" style={{ margin: 0 }}>
                    Rectification acceptée : le responsable des résultats établit une nouvelle
                    version (recalcul motivé), qui suit le même circuit. La version officielle reste
                    en vigueur jusqu’à l’approbation de la nouvelle.
                  </p>
                )}
              </div>
            ))}
          </section>
        )}
        {open && !ranking.can.decideRectification && open.status === 'requested' && open.mine && (
          <Alert tone="info">Votre demande sera tranchée par une autre personne habilitée.</Alert>
        )}
      </CardBody>

      {dialog === 'approve' && (
        <ApproveModal
          warnings={warnings.map((w) => w.message)}
          loading={act.isPending}
          onClose={() => setDialog(null)}
          onConfirm={(note) =>
            act.mutate({
              url: `${base}/approve`,
              body: { note: note || null },
              message: 'Classement approuvé',
            })
          }
        />
      )}
      <ConfirmModal
        open={dialog === 'reject'}
        onClose={() => setDialog(null)}
        title="Renvoyer le classement ?"
        message="Il repasse en brouillon chez le responsable des résultats."
        confirmLabel="Renvoyer"
        reasonRequired
        reasonLabel="Motif du renvoi"
        reasonMinLength={10}
        onConfirm={(reason) =>
          act.mutateAsync({
            url: `${base}/reject`,
            body: { reason },
            message: 'Classement renvoyé',
          })
        }
      />
      <ConfirmModal
        open={dialog === 'provisional' || dialog === 'final'}
        onClose={() => setDialog(null)}
        title={
          dialog === 'final'
            ? 'Publier les résultats définitifs ?'
            : 'Publier les résultats provisoires ?'
        }
        message="Chaque candidat voit son résultat dans son espace ; la liste officielle (matricule, nom, décision des admis — sans note ni rang) devient publique."
        confirmLabel="Publier"
        onConfirm={() =>
          act.mutateAsync({
            url: `${base}/publish`,
            body: { kind: dialog === 'final' ? 'final' : 'provisional' },
            message: 'Résultats publiés',
          })
        }
      />
      <ConfirmModal
        open={dialog === 'rectify'}
        onClose={() => setDialog(null)}
        tone="danger"
        title="Demander une rectification ?"
        message="Circuit d’exception : la demande est tranchée par une autre personne ; si elle est acceptée, une nouvelle version est établie puis approuvée."
        confirmLabel="Demander"
        reasonRequired
        reasonLabel="Motif de la rectification"
        reasonMinLength={10}
        onConfirm={(reason) =>
          act.mutateAsync({
            url: `${base}/rectifications`,
            body: { reason },
            message: 'Rectification demandée',
          })
        }
      />
      <ConfirmModal
        open={dialog === 'accept' || dialog === 'refuse'}
        onClose={() => setDialog(null)}
        tone={dialog === 'accept' ? 'primary' : 'danger'}
        title={dialog === 'accept' ? 'Accepter la rectification ?' : 'Refuser la rectification ?'}
        confirmLabel={dialog === 'accept' ? 'Accepter' : 'Refuser'}
        reasonRequired
        reasonLabel="Motif de la décision"
        reasonMinLength={10}
        onConfirm={(note) =>
          act.mutateAsync({
            url: `/admin/rectifications/${open!.id}/decide`,
            body: { accept: dialog === 'accept', note },
            message: 'Décision enregistrée',
          })
        }
      />
    </Card>
  )
}

function ApproveModal({
  warnings,
  loading,
  onClose,
  onConfirm,
}: {
  warnings: string[]
  loading: boolean
  onClose: () => void
  onConfirm: (note: string) => void
}) {
  const [note, setNote] = useState('')
  return (
    <Modal
      open
      onClose={onClose}
      title="Approuver le classement ?"
      description="Les décisions de passage s’appliquent, le classement est verrouillé et l’étape est close. Toute modification ultérieure passera par une rectification."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" loading={loading} onClick={() => onConfirm(note.trim())}>
            Approuver
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        {warnings.length > 0 && (
          <Alert tone="warning" title="Points d’attention">
            <ul className="sel-issues">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Alert>
        )}
        <Textarea
          label="Visa (observation)"
          rows={3}
          hint="Facultatif : référence de la délibération, observation…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  )
}
